import type { Middleware, UnknownAction } from '@reduxjs/toolkit';
import { track } from './analytics';

interface MutationAction extends UnknownAction {
  payload?: { status?: unknown };
  meta?: {
    arg?: { type?: string; endpointName?: string };
    condition?: boolean;
  };
}

const MUTATION_SETTLED = /^(.+)\/executeMutation\/(fulfilled|rejected)$/;

/**
 * Records every RTK Query mutation as an `Action Performed` event, so what
 * people do in the app is tracked without instrumenting each button. Only the
 * endpoint name and the outcome are sent, never the request arguments.
 *
 * `apis` names the API instances to watch, by reducer path, with the label
 * reports should show: `{ [baseApi.reducerPath]: 'backend' }`. Mutations of
 * any other API are ignored.
 */
export const createAnalyticsMiddleware =
  (apis: Record<string, string>): Middleware =>
  () =>
  (next) =>
  (action) => {
    const result = next(action);

    const { type, payload, meta } = action as MutationAction;
    const match = typeof type === 'string' ? MUTATION_SETTLED.exec(type) : null;
    const api = match && apis[match[1]];
    const endpoint = meta?.arg?.endpointName;
    // `condition` marks a request RTK Query skipped, not one that failed.
    if (!api || !endpoint || meta?.condition) return result;

    const failed = match[2] === 'rejected';
    const errorStatus = payload?.status;
    track('Action Performed', {
      action: endpoint,
      api,
      status: failed ? 'failed' : 'success',
      ...(failed &&
        (typeof errorStatus === 'number' || typeof errorStatus === 'string') && {
          error_status: errorStatus,
        }),
    });

    return result;
  };
